// Connected container — owns its on-demand entity detail query + edit / delete
// / graph mutations via `useEntityRow`. Not storyable (needs QueryClient +
// Router + overlay context). Renders pure inspector sub-parts from
// `EntityInspector` plus the leaf loading / error states.

import { ChevronDown, MapPin } from 'lucide-react'
import type React from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEntityRow } from '@/features/document-overlay/hooks/useEntityRow'
import { OsintEntityLaunchButton } from '@/features/osint/OsintEntityLaunchButton'
import type { DocumentEntity } from '@/services/api/cases'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  EditablePropertiesList,
  EntityActionRow,
  EntityPropertiesList,
  EntitySaveBar,
  EntityStatTriplet,
  InvestigationScoreCard,
  MatchBadge,
} from './EntityInspector'
import { InlineError } from './InlineError'
import { InspectorSkeleton } from './InspectorSkeleton'

export function EntityRow({
  caseId,
  documentId,
  entity,
}: {
  caseId: string
  documentId: string
  entity: DocumentEntity
}) {
  const { t } = useTranslation()
  const [hover, setHover] = useState(false)
  const {
    detailQuery,
    expanded,
    setExpanded,
    editing,
    editedProps,
    isSaving,
    isDeleting,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    entitySummary,
    fullDetail,
    visibleProperties,
    entityDisplayLabel,
    hasGeo,
    handleAddToGraph,
    handleEnterEdit,
    handleCancelEdit,
    handleChangeProp,
    handleSave,
    handleConfirmDelete,
  } = useEntityRow({ caseId, documentId, entity })

  // Stop click-through to the row's expand/collapse handler when the analyst
  // interacts with controls inside the inspector body.
  const stopRowToggle = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
  }, [])

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(p => !p)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(p => !p)
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'relative',
          padding: '12px 14px 12px 14px',
          cursor: 'pointer',
          background: expanded ? 'var(--muted)' : hover ? 'var(--accent)' : 'transparent',
          transition: 'background 0.12s ease',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--foreground)',
            letterSpacing: -0.1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            paddingRight: 18,
          }}
        >
          {/* Phone: a long unbreakable name (an identifier, an address) wraps
              instead of running out of the row under the chevron. */}
          <span className="max-md:min-w-0 max-md:wrap-anywhere">{entityDisplayLabel}</span>
          {hasGeo && (
            <MapPin
              size={12}
              aria-label={t('documents.detail.hasLocation')}
              style={{ flexShrink: 0, color: 'var(--primary)' }}
            />
          )}
        </div>
        {entitySummary && (
          <div
            className="max-md:wrap-anywhere"
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--muted-foreground)',
            }}
          >
            {entitySummary}
          </div>
        )}
        <ChevronDown
          size={13}
          aria-hidden
          style={{
            position: 'absolute',
            top: 14,
            right: 12,
            color: expanded ? 'var(--primary)' : 'var(--muted-foreground)',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.18s ease, color 0.12s ease, opacity 0.12s ease',
            opacity: expanded || hover ? 1 : 0.45,
            pointerEvents: 'none',
          }}
        />
      </div>
      {expanded && (
        <div
          onClick={stopRowToggle}
          onKeyDown={stopRowToggle}
          style={{
            padding: '4px 14px 18px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            background: 'var(--muted)',
            borderTop: '1px solid var(--border)',
          }}
        >
          {detailQuery.isLoading ? (
            <InspectorSkeleton />
          ) : detailQuery.isError ? (
            <InlineError onRetry={() => detailQuery.refetch()} />
          ) : fullDetail ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10 }}>
                <MatchBadge confidence={fullDetail.matchConfidence} />
              </div>
              <InvestigationScoreCard detail={fullDetail} />
              <EntityStatTriplet detail={fullDetail} />
              {editing ? (
                <>
                  <EditablePropertiesList
                    properties={visibleProperties.map(p => ({
                      key: p.key,
                      value: editedProps[p.key] ?? p.value,
                    }))}
                    onChange={handleChangeProp}
                  />
                  <EntitySaveBar
                    onCancel={handleCancelEdit}
                    onSave={handleSave}
                    isSaving={isSaving}
                  />
                </>
              ) : (
                <>
                  <EntityActionRow
                    onAddToGraph={handleAddToGraph}
                    onEdit={handleEnterEdit}
                    onDelete={() => setConfirmDeleteOpen(true)}
                    isDeleting={isDeleting}
                    osintAction={
                      <OsintEntityLaunchButton
                        target={{
                          caseId,
                          entityId: entity.id,
                          entityName: entity.name,
                          entityType: entity.label,
                        }}
                        // As tall as its ActionButton neighbours on a phone.
                        appearance={{ variant: 'ghost', size: 'xs', className: 'max-md:h-9' }}
                      />
                    }
                  />
                  <EntityPropertiesList properties={visibleProperties} />
                </>
              )}
            </>
          ) : null}
        </div>
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <span className="font-semibold text-foreground max-md:wrap-anywhere">
                {entityDisplayLabel}
              </span>{' '}
              and all of its relationships from the case graph. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
